import {
  CellValueChangedEvent,
  ColDef,
  GridReadyEvent,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Layout as AdLayout, Input } from '@surecompanies/core_components'
import axios from 'axios'
import { getMyClients } from 'enrollment-platform/api/persons'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageWrapper from '../../../PageWrapper'
import {
  CensusCsvRowDownloadType,
  updateCensusFileRows,
} from '../api/census-enrollment-platform-api-client'
import { CensusFileRowCountsDto } from '../api/dto/census-file-row-counts.dto'
import { CensusFileRowUpdatesDto } from '../api/dto/census-file-row-updates.dto'
import { CensusFileRowDto } from '../api/dto/census-file-row.dto'
import { CensusFileDto } from '../api/dto/census-file.dto'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { PatchActionEnum } from '../api/enums/patch-action.enum'
import FileInfo from '../components/FileInfo'
import ReviewTabs from '../components/ReviewTabs'
import {
  CENSUS_MANAGEMENT_PATH,
  DEFAULT_PAGINATION_PAGE_SIZE,
} from '../constants'
import { useMarkRowDirtyUpdates } from '../hooks/use-mark-row-dirty-updates'
import style from '../pages/CensusManagement.module.scss'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import { formatIfDate } from '../utils/format-if-date'
import { getInProcessPageData } from '../utils/get-in-process-page-data'
import { getUpdateGridContextMenuItems } from '../utils/get-update-grid-context-menu-items'
import { mapGridDataToCensusFileRowDto } from '../utils/map-grid-data-to-census-file-row-dto'
import AgGridPagination from './AgGridPagination'
import DownloadCensusReportButton from './DownloadCensusReportButton'
import InlineEditActionButtons from './InlineEditActionButtons'
import SendSelectedUpdatesForReviewButton from './SendSelectedUpdatesForReviewButton'
import ToggleEditModeButton from './ToggleEditModeButton'
import WithdrawSelectedButton from './WithdrawSelectedButton'

const { Content } = AdLayout

interface Correction {
  field: string
  newValue: any
}

interface RowWithCorrections {
  allRowData: any
  corrections: Correction[]
}

// Todo: try to incorporate RowData as a data structure for the output
interface _RowData {
  field: string
  liveValue: any
  newValue: any
}

interface UpdateGridRowData {
  field: string
  liveValue: string | number
  newValue: string | number
  employeeName: string
  employeeId: string
  rowId: number
  proposedInitialValue: string | number | boolean | Date
  allRowData: CensusFileRowGridData
}

interface ReviewRecordsBaseProps {
  routeParams: { importPublicKey: string }
  actionButton?: ({
    censusRows,
    handleSubmit,
    selectedRows,
    counts,
  }: {
    censusRows: any[]
    selectedRows: any[]
    handleSubmit: () => void
    counts: CensusFileRowCountsDto | undefined
  }) => React.ReactElement
  rowQueryParams: {
    censusRowType: string
    censusFileRowStatuses: CensusFileRowStatusEnum[]
  }
  handleCellValueChanged?: (
    cellValueChangedEvent: CellValueChangedEvent
  ) => void
  modifyColDefs?: (
    baseColDefs: ColDef<CensusFileRowGridData>[]
  ) => ColDef<CensusFileRowGridData>[]
  setLoading: (loading: boolean) => void
  showSearch?: boolean
}

function ReviewRecordsBaseUpdate({
  routeParams,
  actionButton,
  rowQueryParams,
  setLoading,
  showSearch = false,
}: ReviewRecordsBaseProps) {
  const { censusRowType: paramCensusRowType, censusFileRowStatuses } =
    rowQueryParams

  // Declare all hooks at the top level
  const [counts, setCounts] = useState<CensusFileRowCountsDto | undefined>()
  const [censusFile, setCensusFile] = useState<CensusFileDto | undefined>()
  const { importPublicKey: publicKey } = routeParams
  const [censusRowType, setCensusRowType] = useState<string>(paramCensusRowType)
  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [censusRows, setCensusRows] = useState<CensusFileRowGridData[]>([])
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({})
  const [mode, setMode] = useState<'edit' | 'submit'>('submit')
  const [currentPage, setCurrentPage] = useState(1) // Start on page 1
  const [searchTerm, setSearchTerm] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const gridRef = useRef<GridReadyEvent | undefined>(undefined)
  const clientName = (key) => clients.find((c) => c.id === key)?.name ?? '...'
  const prevRowsRef = useRef<CensusFileRowGridData[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  // Reset logic (clearing selected rows)
  const resetGrid = useCallback(() => {
    gridRef.current?.api.refreshServerSide({ purge: true })
    gridRef.current?.api.deselectAll()
    setSelectedRows([])
    setCheckedItems({})
    setCurrentPage(1)
    setRefreshKey((k) => k + 1)
  }, [])

  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_PAGE_SIZE)

  // Tab change logic
  const onTabChange = useCallback((tab: string) => {
    setCensusRowType(tab)
    setCheckedItems({})
    setSelectedRows([])
    setSearchTerm('')
    setCurrentPage(1)
  }, [])
  const fetchClients = async () => {
    const data = await getMyClients()
    setClients(data.sort((a, b) => a.name.localeCompare(b.name)))
  }
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClients()
    }, 1)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    setLoading(true)
    // Fetch data when component is mounted or censusRowType changes
    const fetchData = async () => {
      try {
        const { rowCounts, data, pagination, censusFile } =
          await getInProcessPageData({
            limit: pageSize,
            page: currentPage,
            publicKey,
            censusRowType,
            censusFileRowStatuses,
            search: searchTerm || undefined,
          })

        setCounts(rowCounts)
        setCensusFile(censusFile)
        setTotalRows(pagination.total ?? 0)
        const mergedData: CensusFileRowGridData[] = data.map((d) => ({
          ...d,
          ...d.rowData,
        }))
        setCensusRows(mergedData)
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching data:', error)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [censusRowType, publicKey, searchTerm, currentPage, pageSize, refreshKey])

  const currentEmployees = censusRows

  const handleSelectAll = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    if (!checked) {
      setCheckedItems({})
      setSelectedRows([])
      return
    }

    if (!totalRows) return

    try {
      setLoading(true)
      const { data } = await getInProcessPageData({
        limit: totalRows,
        page: 1,
        publicKey,
        censusRowType,
        censusFileRowStatuses,
        search: searchTerm || undefined,
      })
      const allRows: CensusFileRowGridData[] = data.map((d) => ({
        ...d,
        ...d.rowData,
      }))
      const newCheckedItems: Record<string, boolean> = {}
      allRows.forEach((row) => {
        newCheckedItems[row.employeeId] = true
      })
      setCheckedItems(newCheckedItems)
      setSelectedRows(allRows)
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching all rows for select all:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  const isAllFullySelected =
    totalRows > 0 && Object.keys(checkedItems).length >= totalRows

  const handleSelectAllInCurrentPageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked

    const newCheckedItems = { ...checkedItems }

    currentEmployees.forEach((row) => {
      if (checked) {
        newCheckedItems[row.employeeId] = true
      } else {
        delete newCheckedItems[row.employeeId]
      }
    })

    setCheckedItems(newCheckedItems)
    setSelectedRows((prev) => {
      const filtered = prev.filter(
        (r) => !currentEmployees.some((cr) => cr.employeeId === r.employeeId)
      )
      return checked ? [...filtered, ...currentEmployees] : filtered
    })
  }
  const isCurrentPageFullySelected =
    currentEmployees.length > 0 &&
    currentEmployees.every((row) => checkedItems[row.employeeId])

  const getActiveBenefits = (
    update: CensusFileRowUpdatesDto | null | undefined
  ): string => {
    if (!update) return ''
    if (Array.isArray(update.activeBenefits)) {
      const uniqueBenefits = update.activeBenefits.reduce<string[]>(
        (acc, benefit) => {
          if (!acc.includes(benefit)) {
            acc.push(benefit)
          }
          return acc
        },
        []
      )
      return uniqueBenefits.join(', ')
    }
    return ''
  }

  const populateUpdateRowData = useCallback(
    (row: CensusFileRowGridData | null | undefined): UpdateGridRowData[] => {
      if (row == null) return []
      const up: UpdateGridRowData[] = []
      ;(row.rowData as Record<string, unknown>).ClientName = clientName

      const pushEntry = (
        key: string,
        liveValueRaw: unknown,
        newValueRaw: unknown
      ) => {
        const liveValue = formatForGrid(liveValueRaw ?? '')
        up.push({
          field: key,
          liveValue:
            typeof liveValue === 'string'
              ? (formatIfDate(liveValue) as string)
              : liveValue,
          newValue: formatForGrid(newValueRaw ?? ''),
          employeeName: `${row.firstName} ${row.lastName}`,
          employeeId: row.employeeId,
          rowId: row.id,
          proposedInitialValue:
            (newValueRaw as string | number | boolean | Date) ?? '',
          allRowData: row,
        })
      }

      const updateFields = row.updates?.updateFields
      if (updateFields) {
        Object.keys(updateFields).forEach((key) => {
          const update = updateFields[key]
          pushEntry(key, update.liveValue, update.newValue)
        })
      }
      return up
    },
    []
  )

  const formatForGrid = (value: any): string | number => {
    if (typeof value === 'boolean') {
      return value ? 'True' : 'False'
    }
    if (value === null || value === undefined) {
      return ''
    }
    return value
  }

  const finalColDefs = useMemo(() => {
    const base: ColDef[] = [
      {
        headerName: 'Field',
        field: 'field',
        flex: 1,
      },
      {
        headerName: 'Current',
        field: 'liveValue',
        flex: 1,
      },
      {
        headerName: 'Proposed',
        field: 'newValue',
        flex: 1,
        editable: (_) => mode === 'edit',
      },
    ]
    return base
  }, [mode])

  // Handle checkbox change for each row
  const handleCheckboxChange = (row) => {
    const isChecked = checkedItems[row.employeeId]
    setCheckedItems((prev) => {
      const next = { ...prev }
      if (next[row.employeeId]) {
        delete next[row.employeeId]
      } else {
        next[row.employeeId] = true
      }
      return next
    })
    if (isChecked) {
      setSelectedRows((prev) =>
        prev.filter((r) => r.employeeId !== row.employeeId)
      )
    } else {
      setSelectedRows((prev) => [
        ...prev.filter((r) => r.employeeId !== row.employeeId),
        row,
      ])
    }
  }

  // Submit and Reject handlers
  const handleSubmit = () => {
    resetGrid()
  }

  useEffect(() => {
    prevRowsRef.current = censusRows
  }, [censusRows])

  const { markRowDirty, dirtyRows, cleanAllRows, undoLastEdit, editCount } =
    useMarkRowDirtyUpdates(prevRowsRef.current, setCensusRows)

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<any>) => {
      const currentCensusRows = [...censusRows] // Take a snapshot before updating

      prevRowsRef.current = currentCensusRows

      // Optionally, mark the row as dirty after updating state (if necessary)
      markRowDirty(params)

      const updatedRows = [...currentCensusRows]
      const updatedRow = updatedRows.find((row) => row.id === params.data.rowId) // Find the edited row
      if (updatedRow) {
        // Access the updates blob inside the row
        const updates = updatedRow?.updates?.updateFields

        // Get the field from params.data (e.g., 'activeBenefits')
        const fieldToUpdate = params.data.field

        if (updates && updates[fieldToUpdate]) {
          // Update the newValue in the updates blob
          updates[fieldToUpdate].newValue = params.newValue // Set the new value for that field

          // Update the censusRows state to trigger a re-render
          setCensusRows(updatedRows) // This will update the grid

          // Optional: Rebuild the row data for AG-Grid if necessary (but this is mostly handled by rowData prop)
        }
      }

      // Refresh the cell to reflect changes (optional)
      params.api.refreshCells({ rowNodes: [params.node] })
    },
    [censusRows, markRowDirty]
  )

  const submitCorrections = async (rowsToSubmit: any[]) => {
    // Group corrections by rowId
    const rowsById: Record<number, RowWithCorrections> = rowsToSubmit.reduce(
      (acc, data) => {
        const rowId = data.rowId
        if (!acc[rowId]) {
          acc[rowId] = {
            allRowData: data.allRowData,
            corrections: [],
          }
        }
        // `corrections` array contains all corrections for a given row
        acc[rowId].corrections.push({
          field: data.field,
          newValue: data.newValue,
        })
        return acc
      },
      {}
    )

    // Apply all corrections to each unique row
    const updatedRows: CensusFileRowDto[] = Object.values(rowsById).map(
      ({ allRowData, corrections }) => {
        corrections.forEach(({ field, newValue }) => {
          if (allRowData?.updates?.updateFields?.[field]) {
            allRowData.updates.updateFields[field].newValue = newValue
          }
          allRowData[field] = newValue
        })
        return mapGridDataToCensusFileRowDto(allRowData)
      }
    )

    await updateCensusFileRows(
      updatedRows,
      publicKey,
      PatchActionEnum.UPDATE_RECORDS
    )
  }

  // Function to generate the clickable employee link
  const generateCoverageSummaryLink = (
    employeePublicKey: string,
    firstName: string,
    lastName: string
  ) => {
    return (
      <a
        href={`/employees/${employeePublicKey}/coverage`}
        target="_blank"
        style={{
          textDecoration: 'underline',
          fontWeight: 'bold',
          color: '#003146',
        }}
        title="Link to the coverage summary"
      >
        {firstName} {lastName}
      </a>
    )
  }

  useEffect(() => {
    prevRowsRef.current = censusRows
  }, [censusRows])

  useEffect(() => {
    if (!containerRef.current) return
    containerRef.current.scrollTop = 0
  }, [currentPage])

  const defaultActionButton = (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
      }}
    >
      <ToggleEditModeButton
        dirtyRows={dirtyRows}
        selectedRows={selectedRows}
        mode={mode} // Pass mode based on the state
        setMode={setMode} // Pass the toggle function to update mode
      />
      {mode === 'edit' ? (
        <InlineEditActionButtons
          undoLastEdit={undoLastEdit}
          editCount={editCount}
          dirtyRows={dirtyRows}
          handleSubmit={handleSubmit}
          cleanAllRows={cleanAllRows}
          submitCorrections={submitCorrections}
          updateMode={true}
          setMode={setMode}
          setLoading={setLoading}
        />
      ) : (
        <>
          <SendSelectedUpdatesForReviewButton
            publicKey={publicKey}
            selectedRows={selectedRows}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
            cleanAllRows={cleanAllRows}
          />
          <WithdrawSelectedButton
            publicKey={publicKey}
            selectedRows={selectedRows}
            handleSubmit={handleSubmit}
            cleanAllRows={cleanAllRows}
            setLoading={setLoading}
            allRows={censusRows}
            counts={counts}
          />
          <DownloadCensusReportButton
            publicKey={publicKey}
            rowType={censusRowType as CensusCsvRowDownloadType}
          />
        </>
      )}
    </div>
  )

  // Render the Ag-Grid
  return (
    <PageWrapper>
      <Page
        title="Review Records"
        action={
          actionButton
            ? actionButton({
                censusRows,
                selectedRows,
                handleSubmit: resetGrid,
                counts,
              })
            : defaultActionButton
        }
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Census Management', path: CENSUS_MANAGEMENT_PATH },
              { title: 'Review Records' },
            ]}
          />
        }
      >
        <AdLayout>
          <Content className={style.content}>
            <div
              style={{
                position: 'relative',
                padding: '15px 15px 0 15px',
              }}
            >
              <FileInfo
                data={censusFile}
                clientName={clientName(censusFile?.clientPublicKey)}
              />
            </div>
            {/* Submit and Reject buttons with count of checked items */}
            <ReviewTabs
              publicKey={publicKey}
              counts={counts}
              onTabChange={onTabChange}
              currentTab={censusRowType}
              style={{ padding: '15px 0 0 15px' }}
            />

            {/* Display Employee Name and ID */}
            <div style={{ margin: '0px 16px' }}>
              <div
                style={{
                  margin: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectAllInCurrentPageChange(e)}
                  checked={isCurrentPageFullySelected}
                />
                <label style={{ fontWeight: 'bold' }}>
                  Select All on This Page
                </label>
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e)}
                  checked={isAllFullySelected}
                />
                <label style={{ fontWeight: 'bold' }}>Select All</label>
              </div>

              {/* Search field */}
              {showSearch && (
                <div style={{ padding: '8px 0' }}>
                  <Input.Search
                    key={censusRowType}
                    placeholder="Search by first or last name"
                    allowClear
                    onSearch={(value) => {
                      setSearchTerm(value.trim())
                      setCurrentPage(1)
                      setCheckedItems({})
                      setSelectedRows([])
                    }}
                    style={{ width: 280 }}
                  />
                </div>
              )}

              {searchTerm && censusRows.length === 0 && (
                <div
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px',
                    background: 'white',
                    borderRadius: '8px',
                  }}
                >
                  No records found
                </div>
              )}
            </div>

            <div
              style={{
                maxHeight: '500px',
                overflow: 'auto',
                position: 'relative',
                border: '1px solid #E0EAF1',
                borderRadius: '8px',
                margin: '10px',
              }}
              ref={containerRef}
            >
              {currentEmployees.map((row, index) => (
                <div
                  key={row.id}
                  className="ag-theme-alpine"
                  style={{
                    backgroundColor: checkedItems[row.employeeId]
                      ? 'var(--SureCo-Medium-Blue, #E0EAF1)'
                      : 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    font: 'SF Pro Text',
                    width: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div key={index}>
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={checkedItems[row.employeeId] || false}
                      disabled={mode === 'edit'}
                      onChange={() => handleCheckboxChange(row)} // Only change state for this row
                      style={{
                        marginRight: '10px',
                        backgroundColor: checkedItems[row.employeeId]
                          ? 'var(--Components-Checkbox-Global-colorWhite, #FFFFFF)'
                          : 'white',
                      }} // Space between checkbox and text
                    />
                    {/* Display Employee Name and ID once at the top of each section */}
                    <div
                      style={{
                        marginBottom: '20px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'inline',
                        padding: '10px',
                      }}
                    >
                      {generateCoverageSummaryLink(
                        row?.updates?.employeePublicKey ?? '',
                        row.firstName,
                        row.lastName
                      )}
                    </div>
                    {/* Display Employee ID with a different color */}
                    <div
                      style={{
                        marginBottom: '20px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#999999',
                        display: 'inline',
                        padding: '10px',
                      }}
                    >
                      (EmployeeID: {row.employeeId})
                    </div>
                    <div
                      style={{
                        fontSize: '14px',
                        color: '#999999',
                        marginTop: '10px',
                      }}
                    >
                      {getActiveBenefits(row.updates)}{' '}
                      {/* Display active benefits */}
                    </div>
                  </div>
                  <div style={{ marginTop: '15px' }}></div>
                  <div className="ag-theme-alpine">
                    {/* Ag-Grid React component */}
                    <AgGridReact
                      columnDefs={finalColDefs}
                      rowData={populateUpdateRowData(row)}
                      domLayout="autoHeight"
                      enableCellTextSelection
                      ensureDomOrder
                      getContextMenuItems={getUpdateGridContextMenuItems}
                      onCellValueChanged={onCellValueChanged}
                    />
                  </div>
                </div>
              ))}
            </div>
            <AgGridPagination
              key={`${censusRowType}-${searchTerm}`}
              totalSize={totalRows}
              setCurrentPage={setCurrentPage}
              setPageSize={(size) => {
                if (size !== pageSize) {
                  setCurrentPage(1)
                }
                setPageSize(size)
              }}
            />
          </Content>
        </AdLayout>
      </Page>
    </PageWrapper>
  )
}

export default ReviewRecordsBaseUpdate
